/**
 * User manual: loading and helpers.
 *
 * The manual is private. Its content lives in frontend/manual/, outside src/
 * and public/, so none of it is built into the public site; the frontend
 * deploy uploads it to a private bucket and it is read here, after login,
 * through the bedrock service (GET /bedrock/manual/...). The same files are
 * read as-is by the Nuclino publisher and the release-notes drafter in
 * scripts/manual/.
 */

import { useEffect, useState } from 'react';
import manualService from '../services/manualService';

const LAST_SEEN_KEY = 'manual.lastSeenReleaseNote';

// One request per page load, shared by the layout and the Help page. A failed
// request isn't kept, so the next caller tries again.
let indexPromise = null;
// The resolved index, so components mounted later (the layout remounts on
// every navigation) start from it instead of flashing a loading state.
let loadedIndex = null;

const normalizeIndex = (data) => ({
  sections: data?.sections || [],
  apps: data?.apps || [],
  // Newest first; ids are YYYY-MM-DD with an optional -N suffix, so they sort as strings.
  releaseNotes: [...(data?.releaseNotes || [])].sort((a, b) => b.id.localeCompare(a.id)),
});

export const loadManualIndex = () => {
  if (!indexPromise) {
    indexPromise = manualService
      .getIndex()
      .then((data) => {
        loadedIndex = normalizeIndex(data);
        return loadedIndex;
      })
      .catch((error) => {
        indexPromise = null;
        throw error;
      });
  }
  return indexPromise;
};

/** Forget the loaded index (for tests). Every user gets the same index, so nothing needs clearing on logout. */
export const resetManualIndex = () => {
  indexPromise = null;
  loadedIndex = null;
};

/** `{ status: 'loading' | 'ready' | 'error', index }` for the manual index. */
export const useManualIndex = () => {
  const [state, setState] = useState(() =>
    loadedIndex ? { status: 'ready', index: loadedIndex } : { status: 'loading', index: null }
  );

  useEffect(() => {
    // Also runs when the state started from loadedIndex: it resolves straight
    // away from the shared promise, and covers an index that arrived between
    // this component's first render and this effect.
    let cancelled = false;
    loadManualIndex()
      .then((index) => {
        if (!cancelled) setState((prev) => (prev.index === index ? prev : { status: 'ready', index }));
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', index: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};

/** A page's Markdown; the server has already removed owners' review notes. */
export const fetchManualPage = (path) => manualService.getPage(path);

export const getApp = (index, id) => index?.apps.find((app) => app.id === id) || null;

/** The app whose route owns `pathname` (exact match or a sub-path of it). */
export const findAppForPath = (index, pathname) => {
  if (!index || !pathname) return null;
  return index.apps.find((app) => app.route && (pathname === app.route || pathname.startsWith(`${app.route}/`))) || null;
};

/**
 * Listed in an app's `permissions` when its route needs only a login
 * (ProtectedRoute) rather than a granted permission.
 */
export const ANY_SIGNED_IN_USER = 'authenticated';

/** Mirrors the route guard: any listed permission is enough. */
export const canAccess = (app, auth) => {
  if (!app || !auth?.user || !auth.hasPermission) return false;
  return app.permissions.some((permission) => permission === ANY_SIGNED_IN_USER || auth.hasPermission(permission));
};

export const appPagePath = (app) => `apps/${app.id}.md`;
export const releaseNotePath = (note) => `release-notes/${note.id}.md`;

export const getLastSeenReleaseNote = () => {
  try {
    return localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    return null;
  }
};

export const markReleaseNotesSeen = (index) => {
  if (!index?.releaseNotes.length) return;
  try {
    localStorage.setItem(LAST_SEEN_KEY, index.releaseNotes[0].id);
  } catch {
    // Private windows and blocked storage just mean the dot keeps showing.
  }
};

export const hasUnseenReleaseNotes = (index) => {
  if (!index?.releaseNotes.length) return false;
  const lastSeen = getLastSeenReleaseNote();
  return !lastSeen || index.releaseNotes[0].id.localeCompare(lastSeen) > 0;
};
